import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Checkbox,
  Radio,
  FormControlLabel,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  useMediaQuery,
} from '@mui/material';
import { InputAdornment } from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Home as HomeIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CreditCard as CreditCardIcon,
} from '@mui/icons-material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { getFirestore, collection, addDoc } from 'firebase/firestore';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { getCurrentDateISO, getMadridTime, getUserIP } from '../../utils/dateUtils';
import CustomDateInput from './CustomDateInput';
import { app } from '../../shared/firebase';


// Custom theme for the client portal
const clientTheme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 500,
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          padding: '10px 24px',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
  },
});

const steps = [
  { label: 'Personal', icon: <PersonIcon /> },
  { label: 'Profesional', icon: <BusinessIcon /> },
  { label: 'Vivienda', icon: <HomeIcon /> },
  { label: 'Créditos', icon: <CreditCardIcon /> },
];

const MORTGAGE_FINANCIAL_OPTIONS = [
  { value: 'con-ahorros', label: 'Tenemos ahorros para la hipoteca' },
  { value: 'sin-ahorros', label: 'Nos gustaría evaluar hipoteca sin ahorros, más gastos' },
  { value: 'con-y-sin-ahorros', label: 'Nos gustaría evaluar hipoteca con y sin ahorros' },
];

// Function to get the next case number
const getNextClientCaseNumber = async () => {
  try {
    // For now, use a simple timestamp-based approach
    // This can be replaced with Cloud Function call once deployed
    const timestamp = Date.now();
    const datePrefix = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const uniqueNumber = String(timestamp % 100000000).padStart(8, '0');
    return `${datePrefix}${uniqueNumber}`;
  } catch (error) {
    console.error('Error generating case number:', error);
    // Fallback: generate a timestamp-based number
    const timestamp = Date.now();
    const datePrefix = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const uniqueNumber = String(timestamp % 100000000).padStart(8, '0');
    return `${datePrefix}${uniqueNumber}`;
  }
};

const ClientPortal = ({ mortgageMode = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const portalSteps = mortgageMode
    ? steps.map((step, index) => index === steps.length - 1
      ? { ...step, label: 'Información Financiera' }
      : step)
    : steps;
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [preflightChecking, setPreflightChecking] = useState(true);
  const [preflightPassed, setPreflightPassed] = useState(null);
  const [preflightRefId, setPreflightRefId] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    primerTitular: {
      nombre: '',
      apellidos: '',
      dni: '',
      fechaNacimiento: '',
      nacionalidad: '',
      direccionPostal: '',
      poblacion: '',
      provincia: '',
      codigoPostal: '',
      movil: '',
      email: '',
    },
    segundoTitular: {
      nombre: '',
      apellidos: '',
      dni: '',
      fechaNacimiento: '',
      nacionalidad: '',
      direccionPostal: '',
      poblacion: '',
      provincia: '',
      codigoPostal: '',
      movil: '',
      email: '',
    },
    primerTitularProfesional: {
      profesion: '',
      salarioNetoMensual: '',
      numeroPagas: '12',
      tipoContrato: 'Indefinido',
      antiguedadLaboral: '',
    },
    segundoTitularProfesional: {
      profesion: '',
      salarioNetoMensual: '',
      numeroPagas: '12',
      tipoContrato: 'Indefinido',
      antiguedadLaboral: '',
    },
    gastosVivienda: {
      tipoVivienda: '', // Single selection: 'propiedadSinHipoteca', 'propiedadConHipoteca', 'alquiler', 'otros'
      importeAlquiler: '',
      importeHipoteca: '',
    },
    otrosDatos: {
      estadoCivil: 'Soltero',
      numeroHijos: '',
      otrosCreditosPrestamos: '',
      importeMensual: '',
    },
    creditos: [{
      id: 'default-empty',
      banco: '',
      tipoCredito: '',
      capitalPendiente: '',
      cuotaMensual: '',
      situacion: '',
    }],
    informacionFinanciera: {
      opcion: '',
      importeAhorros: '',
    },
    firmas: {
      vedaconClause: {
        primerTitular: false,
        segundoTitular: false,
      },
      applicationSignature: {
        primerTitular: false,
        segundoTitular: false,
      },
      signatures: {
        vedaconClause: {
          primerTitular: '',
          segundoTitular: '',
        },
        applicationSignature: {
          primerTitular: '',
          segundoTitular: '',
        },
      },
    },
    acceptSubmitConfirmation: false,
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showSecondHolder, setShowSecondHolder] = useState(false);


  // Number formatting helpers for European style (es-ES)
  const formatEuroNumber = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const num = Number(value);
    if (Number.isNaN(num)) return '';
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  // Normalize user input like "12.345,67" => "12345.67"
  const normalizeEuropeanInput = (input) => {
    if (typeof input !== 'string') input = String(input ?? '');
    const cleaned = input.replace(/[^0-9.,]/g, '');
    if (cleaned === '') return '';
    if (cleaned.includes(',')) {
      const parts = cleaned.split(',');
      const integerPart = parts.slice(0, -1).join(',').replace(/[.,]/g, '');
      const decimalPart = parts[parts.length - 1].replace(/\./g, '');
      return `${integerPart}.${decimalPart}`;
    }
    // No comma: treat dots as thousand separators
    return cleaned.replace(/\./g, '');
  };

  // Firebase anonymous authentication with state listener
  useEffect(() => {
    const auth = getAuth(app);

    const initializeAuth = async () => {
      try {
        setAuthLoading(true);

        // Check if user is already signed in (from localStorage persistence)
        const currentUser = auth.currentUser;

        if (currentUser && currentUser.isAnonymous) {
          // User already exists from previous session
          const user = {
            uid: currentUser.uid,
            email: currentUser.email || 'anonymous@portal.com',
            displayName: 'Usuario del Portal',
            isAnonymous: currentUser.isAnonymous
          };
          setUser(user);
          setAuthLoading(false);
          return;
        }

        // Sign in anonymously if no user exists
        try {
          const userCredential = await signInAnonymously(auth);
          const firebaseUser = userCredential.user;

          const user = {
            uid: firebaseUser.uid,
            email: firebaseUser.email || 'anonymous@portal.com',
            displayName: 'Usuario del Portal',
            isAnonymous: firebaseUser.isAnonymous
          };

          setUser(user);
          setAuthLoading(false);
        } catch (signInError) {
          // Handle network errors more gracefully
          if (signInError.code === 'auth/network-request-failed') {
            console.warn('Network error during auth. Will retry when connection is restored.');
            // Don't set error immediately - let the auth state listener handle it
            setAuthLoading(false);
          } else {
            throw signInError;
          }
        }
      } catch (error) {
        console.error('Authentication error:', error);
        setError('Error de inicialización. Por favor, recarga la página.');
        setAuthLoading(false);
      }
    };

    // Listen for auth state changes (handles token expiration, sign-outs, etc.)
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser && firebaseUser.isAnonymous) {
        // User is signed in (or restored from persistence)
        const user = {
          uid: firebaseUser.uid,
          email: firebaseUser.email || 'anonymous@portal.com',
          displayName: 'Usuario del Portal',
          isAnonymous: firebaseUser.isAnonymous
        };
        setUser(user);
        setAuthLoading(false);
      } else if (!firebaseUser) {
        // User was signed out (e.g., token expired, network issue)
        // Try to re-authenticate
        try {
          await initializeAuth();
        } catch (error) {
          console.error('Failed to re-authenticate:', error);
          // Show error message for re-authentication failure
          setError('Sesión expirada. Intentando reconectar...');
        }
      }
    });

    // Initialize auth if no listener has fired yet
    if (!auth.currentUser) {
      initializeAuth();
    } else {
      // User already exists, set immediately
      const currentUser = auth.currentUser;
      if (currentUser && currentUser.isAnonymous) {
        const user = {
          uid: currentUser.uid,
          email: currentUser.email || 'anonymous@portal.com',
          displayName: 'Usuario del Portal',
          isAnonymous: currentUser.isAnonymous
        };
        setUser(user);
        setAuthLoading(false);
      }
    }

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, []);

  // Preflight: verify auth is working (no Firestore write — saves cost)
  useEffect(() => {
    const runPreflight = async () => {
      if (authLoading) return;
      if (!user) return;

      setPreflightChecking(true);
      try {
        // Simply verify the auth token is valid — no Firestore write needed.
        const auth = getAuth(app);
        const currentUser = auth.currentUser;
        if (currentUser) {
          await currentUser.getIdToken(/* forceRefresh */ false);
        }
        // If we got here, connectivity + auth is fine
        setPreflightPassed(true);
        setPreflightChecking(false);
        setPreflightRefId(null);
      } catch (preErr) {
        console.error('Preflight auth check failed:', preErr);
        setPreflightRefId(null);
        setPreflightPassed(false);
        setPreflightChecking(false);
        setError('Error de conexión. Cambie de navegador o red (desactive VPN/proxy o intente con datos móviles).');
      }
    };

    runPreflight();
  }, [authLoading, user]);



  const handleNext = () => {
    // Validate housing step before proceeding to credits
    if (activeStep === 2) { // Housing step - validate before proceeding to credits
      if (!formData.gastosVivienda.tipoVivienda) {
        setError('Debe seleccionar un tipo de vivienda para continuar.');
        return;
      }
    }

    // Credit step is optional, no validation needed

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setError(''); // Clear any previous errors
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleNestedInputChange = (section, subsection, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subsection]: {
          ...prev[section][subsection],
          [field]: value,
        },
      },
    }));
  };

  // Credit handling functions
  const addCredit = () => {
    const newCredit = {
      id: `credit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // Unique ID generation
      banco: '',
      tipoCredito: '',
      capitalPendiente: '',
      cuotaMensual: '',
      situacion: '',
    };
    setFormData(prev => ({
      ...prev,
      creditos: [...prev.creditos, newCredit],
    }));
  };

  const updateCredit = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      creditos: prev.creditos.map(credit =>
        credit.id === id ? { ...credit, [field]: value } : credit
      ),
    }));
  };

  const removeCredit = (id) => {
    setFormData(prev => {
      // Don't allow removing the last credit if it's the default empty one
      if (prev.creditos.length === 1 && prev.creditos[0].id === 'default-empty') {
        return prev;
      }
      return {
        ...prev,
        creditos: prev.creditos.filter(credit => credit.id !== id),
      };
    });
  };


  const handleSubmit = async () => {
    if (mortgageMode) {
      const { opcion, importeAhorros } = formData.informacionFinanciera;
      if (!opcion || (opcion === 'con-ahorros' && !String(importeAhorros).trim())) {
        setError('Seleccione una opción de ahorro e indique el importe si corresponde.');
        return;
      }
    }

    // Require acceptance of submission confirmation
    if (!formData.acceptSubmitConfirmation) {
      setError('Debe aceptar los términos y la política de privacidad antes de continuar.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Check if user is available (from our mock user system)
      if (!user) {
        throw new Error('Usuario no disponible. Por favor, recarga la página.');
      }

      const caseNumber = await getNextClientCaseNumber();

      // Get Madrid time and user IP
      const madridTime = getMadridTime();
      const userIP = await getUserIP();

      // Transform data for storage
      const transformedData = {
        ...formData,
        caseNumber,
        statusChangeDate: getCurrentDateISO(),
        registeredDate: getCurrentDateISO(),
        status: 'En Revisión',
        bankGroup: mortgageMode ? 'HIPOTECAS' : 'COFIDIS',
        source: mortgageMode ? 'mortgage-portal' : 'client-portal',
        userId: user.uid,
        userEmail: user.email || 'anonymous@portal.com',
        userName: user.displayName || 'Usuario del Portal',
        // Madrid time at case creation
        madridCreateHour: madridTime.hour,
        madridCreateMinutes: madridTime.minutes,
        madridCreateTime: madridTime.timeString,
        // User IP address
        userIP: userIP,
      };


      console.log('ClientPortal - Form data before submission:', formData);
      console.log('ClientPortal - Creditos before submission:', formData.creditos);
      console.log('ClientPortal - Creditos length:', formData.creditos?.length);
      console.log('ClientPortal - Transformed data:', transformedData);
      console.log('ClientPortal - Transformed creditos:', transformedData.creditos);
      console.log('ClientPortal - Transformed creditos length:', transformedData.creditos?.length);

      // Merge professional data into main holder objects
      if (transformedData.primerTitularProfesional) {
        transformedData.primerTitular = {
          ...transformedData.primerTitular,
          ...transformedData.primerTitularProfesional
        };
        delete transformedData.primerTitularProfesional;
      }

      if (transformedData.segundoTitularProfesional) {
        transformedData.segundoTitular = {
          ...transformedData.segundoTitular,
          ...transformedData.segundoTitularProfesional
        };
        delete transformedData.segundoTitularProfesional;
      }

      // Transform housing data - now already in the correct format
      if (transformedData.gastosVivienda) {
        // Ensure housing type is selected and amounts are numbers
        transformedData.gastosVivienda = {
          tipoVivienda: transformedData.gastosVivienda.tipoVivienda || '',
          importeHipoteca: Number(transformedData.gastosVivienda.importeHipoteca) || 0,
          importeAlquiler: Number(transformedData.gastosVivienda.importeAlquiler) || 0,
        };
      }

      if (mortgageMode && transformedData.informacionFinanciera) {
        transformedData.informacionFinanciera = {
          opcion: transformedData.informacionFinanciera.opcion || '',
          importeAhorros: Number(normalizeEuropeanInput(transformedData.informacionFinanciera.importeAhorros)) || 0,
        };
      }

      const db = getFirestore(app);
      console.log('Original form data:', formData);
      console.log('Transformed data:', transformedData);
      console.log('Current user:', user);
      console.log('Firebase auth state:', getAuth(app).currentUser);
      console.log('Attempting to save to cofidisCases collection...');

      try {
        console.log('About to save to database - Final transformedData:', transformedData);
        console.log('About to save to database - Creditos in final data:', transformedData.creditos);
        const docRef = await addDoc(collection(db, 'cofidisCases'), transformedData);
        console.log('Document written with ID: ', docRef.id);
        console.log('Successfully saved credit data:', transformedData.creditos);
      } catch (error) {
        console.error('Firestore error details:', error);
        throw error;
      }

      setSuccess(true);
      setLoading(false);
    } catch (error) {
      console.error('Error submitting form:', error);

      // Show user-friendly error
      const code = error?.code || '';
      let msg = 'Se ha producido un error al enviar el formulario.';
      if (code === 'permission-denied') {
        msg = 'Permisos insuficientes. Verifique que su sesión esté activa y vuelva a intentarlo más tarde.';
      }
      setError(msg);
      setLoading(false);
    }
  };

  const renderPersonalDataStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom color="primary">
        Datos del Primer Titular
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Nombre"
            value={formData.primerTitular.nombre}
            onChange={(e) => handleInputChange('primerTitular', 'nombre', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Apellidos"
            value={formData.primerTitular.apellidos}
            onChange={(e) => handleInputChange('primerTitular', 'apellidos', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="DNI"
            value={formData.primerTitular.dni}
            onChange={(e) => handleInputChange('primerTitular', 'dni', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomDateInput
            label="Fecha de Nacimiento"
            value={formData.primerTitular.fechaNacimiento}
            onChange={(value) => handleInputChange('primerTitular', 'fechaNacimiento', value)}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Nacionalidad"
            value={formData.primerTitular.nacionalidad}
            onChange={(e) => handleInputChange('primerTitular', 'nacionalidad', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Dirección Postal"
            value={formData.primerTitular.direccionPostal}
            onChange={(e) => handleInputChange('primerTitular', 'direccionPostal', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Población"
            value={formData.primerTitular.poblacion}
            onChange={(e) => handleInputChange('primerTitular', 'poblacion', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Provincia"
            value={formData.primerTitular.provincia}
            onChange={(e) => handleInputChange('primerTitular', 'provincia', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Código Postal"
            value={formData.primerTitular.codigoPostal}
            onChange={(e) => handleInputChange('primerTitular', 'codigoPostal', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Móvil"
            value={formData.primerTitular.movil}
            onChange={(e) => handleInputChange('primerTitular', 'movil', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.primerTitular.email}
            onChange={(e) => handleInputChange('primerTitular', 'email', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Estado Civil</InputLabel>
            <Select
              value={formData.otrosDatos.estadoCivil}
              onChange={(e) => handleInputChange('otrosDatos', 'estadoCivil', e.target.value)}
              label="Estado Civil"
            >
              <MenuItem value="Soltero">Soltero</MenuItem>
              <MenuItem value="Casado">Casado</MenuItem>
              <MenuItem value="Divorciado">Divorciado</MenuItem>
              <MenuItem value="Viudo">Viudo</MenuItem>
              <MenuItem value="Pareja de hecho">Pareja de hecho</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Número de Hijos"
            type="number"
            value={formData.otrosDatos.numeroHijos}
            onChange={(e) => handleInputChange('otrosDatos', 'numeroHijos', e.target.value)}
          />
        </Grid>
      </Grid>

      <Box mt={4}>
        <Button
          variant="outlined"
          onClick={() => setShowSecondHolder(!showSecondHolder)}
          startIcon={showSecondHolder ? <PersonIcon /> : <PersonIcon />}
        >
          {showSecondHolder ? 'Ocultar Segundo Titular' : 'Agregar Segundo Titular'}
        </Button>
      </Box>

      {showSecondHolder && (
        <Box mt={4}>
          <Typography variant="h6" gutterBottom color="primary">
            Datos del Segundo Titular
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre"
                value={formData.segundoTitular.nombre}
                onChange={(e) => handleInputChange('segundoTitular', 'nombre', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Apellidos"
                value={formData.segundoTitular.apellidos}
                onChange={(e) => handleInputChange('segundoTitular', 'apellidos', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="DNI"
                value={formData.segundoTitular.dni}
                onChange={(e) => handleInputChange('segundoTitular', 'dni', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomDateInput
                label="Fecha de Nacimiento"
                value={formData.segundoTitular.fechaNacimiento}
                onChange={(value) => handleInputChange('segundoTitular', 'fechaNacimiento', value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nacionalidad"
                value={formData.segundoTitular.nacionalidad}
                onChange={(e) => handleInputChange('segundoTitular', 'nacionalidad', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Dirección Postal"
                value={formData.segundoTitular.direccionPostal}
                onChange={(e) => handleInputChange('segundoTitular', 'direccionPostal', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Población"
                value={formData.segundoTitular.poblacion}
                onChange={(e) => handleInputChange('segundoTitular', 'poblacion', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Provincia"
                value={formData.segundoTitular.provincia}
                onChange={(e) => handleInputChange('segundoTitular', 'provincia', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Código Postal"
                value={formData.segundoTitular.codigoPostal}
                onChange={(e) => handleInputChange('segundoTitular', 'codigoPostal', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Móvil"
                value={formData.segundoTitular.movil}
                onChange={(e) => handleInputChange('segundoTitular', 'movil', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.segundoTitular.email}
                onChange={(e) => handleInputChange('segundoTitular', 'email', e.target.value)}
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );

  const renderProfessionalDataStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom color="primary">
        Datos Profesionales del Primer Titular
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Profesión"
            value={formData.primerTitularProfesional.profesion}
            onChange={(e) => handleInputChange('primerTitularProfesional', 'profesion', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Salario Neto Mensual"
            type="number"
            value={formData.primerTitularProfesional.salarioNetoMensual}
            onChange={(e) => handleInputChange('primerTitularProfesional', 'salarioNetoMensual', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Número de Pagas"
            type="number"
            value={formData.primerTitularProfesional.numeroPagas}
            onChange={(e) => handleInputChange('primerTitularProfesional', 'numeroPagas', e.target.value)}
            inputProps={{ min: 1, max: 16 }}
            helperText="Ej: 12 (mensual), 14 (extra)"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Tipo de Contrato</InputLabel>
            <Select
              value={formData.primerTitularProfesional.tipoContrato}
              onChange={(e) => handleInputChange('primerTitularProfesional', 'tipoContrato', e.target.value)}
              label="Tipo de Contrato"
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: '60vh',
                    width: 'auto',
                    minWidth: '200px',
                    maxWidth: '90vw',
                  },
                  sx: {
                    '& .MuiMenu-list': {
                      padding: '8px 0',
                    },
                    '& .MuiMenuItem-root': {
                      fontSize: '14px',
                      padding: '12px 16px',
                      minHeight: 'auto',
                    },
                  },
                },
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'center',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'center',
                },
                disableScrollLock: true,
                disablePortal: false,
                keepMounted: false,
                variant: 'menu',
              }}
            >
              <MenuItem value="Indefinido">Indefinido</MenuItem>
              <MenuItem value="Fijo Discontinuo">Fijo Discontinuo</MenuItem>
              <MenuItem value="Autonomo">Autonomo</MenuItem>
              <MenuItem value="Funcionario">Funcionario</MenuItem>
              <MenuItem value="Temporal">Temporal</MenuItem>
              <MenuItem value="Jubilado">Jubilado</MenuItem>
              <MenuItem value="Desempleado">Desempleado</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomDateInput
            label="Antigüedad Laboral"
            value={formData.primerTitularProfesional.antiguedadLaboral}
            onChange={(value) => handleInputChange('primerTitularProfesional', 'antiguedadLaboral', value)}
          />
        </Grid>
      </Grid>

      {showSecondHolder && (
        <Box mt={4}>
          <Typography variant="h6" gutterBottom color="primary">
            Datos Profesionales del Segundo Titular
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Profesión"
                value={formData.segundoTitularProfesional.profesion}
                onChange={(e) => handleInputChange('segundoTitularProfesional', 'profesion', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Salario Neto Mensual"
                type="number"
                value={formData.segundoTitularProfesional.salarioNetoMensual}
                onChange={(e) => handleInputChange('segundoTitularProfesional', 'salarioNetoMensual', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Número de Pagas"
                type="number"
                value={formData.segundoTitularProfesional.numeroPagas}
                onChange={(e) => handleInputChange('segundoTitularProfesional', 'numeroPagas', e.target.value)}
                inputProps={{ min: 1, max: 16 }}
                helperText="Ej: 12 (mensual), 14 (extra)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Contrato</InputLabel>
                <Select
                  value={formData.segundoTitularProfesional.tipoContrato}
                  onChange={(e) => handleInputChange('segundoTitularProfesional', 'tipoContrato', e.target.value)}
                  label="Tipo de Contrato"
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: '60vh',
                        width: 'auto',
                        minWidth: '200px',
                        maxWidth: '90vw',
                      },
                      sx: {
                        '& .MuiMenu-list': {
                          padding: '8px 0',
                        },
                        '& .MuiMenuItem-root': {
                          fontSize: '14px',
                          padding: '12px 16px',
                          minHeight: 'auto',
                        },
                      },
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'center',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'center',
                    },
                    disableScrollLock: true,
                    disablePortal: false,
                    keepMounted: false,
                    variant: 'menu',
                  }}
                >
                  <MenuItem value="Indefinido">Indefinido</MenuItem>
                  <MenuItem value="Fijo Discontinuo">Fijo Discontinuo</MenuItem>
                  <MenuItem value="Autonomo">Autonomo</MenuItem>
                  <MenuItem value="Funcionario">Funcionario</MenuItem>
                  <MenuItem value="Temporal">Temporal</MenuItem>
                  <MenuItem value="Jubilado">Jubilado</MenuItem>
                  <MenuItem value="Desempleado">Desempleado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomDateInput
                label="Antigüedad Laboral"
                value={formData.segundoTitularProfesional.antiguedadLaboral}
                onChange={(value) => handleInputChange('segundoTitularProfesional', 'antiguedadLaboral', value)}
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );

  const renderHousingDataStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom color="primary">
        Datos de Vivienda
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Seleccione UNA opción que corresponda:
      </Typography>
      <FormControl component="fieldset">
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Radio
                  checked={formData.gastosVivienda.tipoVivienda === 'propiedadSinHipoteca'}
                  onChange={(e) => handleInputChange('gastosVivienda', 'tipoVivienda', 'propiedadSinHipoteca')}
                  value="propiedadSinHipoteca"
                />
              }
              label="Propiedad sin hipoteca"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Radio
                  checked={formData.gastosVivienda.tipoVivienda === 'propiedadConHipoteca'}
                  onChange={(e) => handleInputChange('gastosVivienda', 'tipoVivienda', 'propiedadConHipoteca')}
                  value="propiedadConHipoteca"
                />
              }
              label="Propiedad con hipoteca"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Radio
                  checked={formData.gastosVivienda.tipoVivienda === 'alquiler'}
                  onChange={(e) => handleInputChange('gastosVivienda', 'tipoVivienda', 'alquiler')}
                  value="alquiler"
                />
              }
              label="Alquiler"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Radio
                  checked={formData.gastosVivienda.tipoVivienda === 'otros'}
                  onChange={(e) => handleInputChange('gastosVivienda', 'tipoVivienda', 'otros')}
                  value="otros"
                />
              }
              label="Otros"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Importe Alquiler"
              type="number"
              value={formData.gastosVivienda.importeAlquiler}
              onChange={(e) => handleInputChange('gastosVivienda', 'importeAlquiler', e.target.value)}
              disabled={formData.gastosVivienda.tipoVivienda !== 'alquiler'}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Importe Hipoteca"
              type="number"
              value={formData.gastosVivienda.importeHipoteca}
              onChange={(e) => handleInputChange('gastosVivienda', 'importeHipoteca', e.target.value)}
              disabled={formData.gastosVivienda.tipoVivienda !== 'propiedadConHipoteca'}
            />
          </Grid>
        </Grid>
      </FormControl>
    </Box>
  );

  const renderCreditDataStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom color="primary">
        {mortgageMode ? 'Información Financiera' : 'Información de Créditos'}
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 3 }}>
        {mortgageMode
          ? 'Indique cómo desea evaluar la hipoteca y añada sus créditos y préstamos actuales.'
          : 'Agregue información sobre sus créditos y préstamos actuales. Puede agregar múltiples entradas.'}
      </Typography>

      {mortgageMode && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Información Financiera
            </Typography>
            <FormControl fullWidth required>
              <InputLabel id="mortgage-financial-option-label">Preferencia para la hipoteca</InputLabel>
              <Select
                labelId="mortgage-financial-option-label"
                value={formData.informacionFinanciera.opcion}
                label="Preferencia para la hipoteca"
                onChange={(e) => handleInputChange('informacionFinanciera', 'opcion', e.target.value)}
              >
                {MORTGAGE_FINANCIAL_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {formData.informacionFinanciera.opcion === 'con-ahorros' && (
              <TextField
                fullWidth
                required
                label="Importe de ahorros disponibles"
                type="number"
                value={formData.informacionFinanciera.importeAhorros}
                onChange={(e) => handleInputChange('informacionFinanciera', 'importeAhorros', e.target.value)}
                inputProps={{ min: 0, step: '0.01' }}
                InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                sx={{ mt: 2 }}
              />
            )}
          </CardContent>
        </Card>
      )}

      {mortgageMode && (
        <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 2 }}>
          INDICANOS TUS FINANCIACIONES ACTUALES
        </Typography>
      )}

      {/* Credits Table (desktop) or Cards (mobile) */}
      {formData.creditos.length > 0 ? (
        isMobile ? (
          <Box sx={{ display: 'grid', gap: 2, mb: 3 }}>
            {formData.creditos.map((credit) => (
              <Card key={credit.id} variant="outlined">
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Banco / Financiera"
                        size="small"
                        value={credit.banco}
                        onChange={(e) => updateCredit(credit.id, 'banco', e.target.value)}
                        placeholder="Ej: Caixabank"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel shrink id={`tipo-credito-label-${credit.id}`}>Tipo Crédito</InputLabel>
                        <Select
                          labelId={`tipo-credito-label-${credit.id}`}
                          id={`tipo-credito-${credit.id}`}
                          value={credit.tipoCredito}
                          label="Tipo Crédito"
                          onChange={(e) => updateCredit(credit.id, 'tipoCredito', e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="">Seleccionar</MenuItem>
                          <MenuItem value="Tarjeta de crédito">Tarjeta de crédito</MenuItem>
                          <MenuItem value="Préstamo personal">Préstamo personal</MenuItem>
                          <MenuItem value="Crédito vehicular">Crédito vehicular</MenuItem>
                          <MenuItem value="Hipoteca">Hipoteca</MenuItem>
                          <MenuItem value="Otros">Otros</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Capital Pendiente"
                        InputLabelProps={{ shrink: true }}
                        value={credit.capitalPendiente}
                        onChange={(e) => updateCredit(credit.id, 'capitalPendiente', e.target.value)}
                        placeholder="0,00"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">€</InputAdornment>,
                          inputMode: 'decimal',
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Cuota Mensual"
                        InputLabelProps={{ shrink: true }}
                        value={credit.cuotaMensual}
                        onChange={(e) => updateCredit(credit.id, 'cuotaMensual', e.target.value)}
                        placeholder="0,00"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">€</InputAdornment>,
                          inputMode: 'decimal',
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel shrink id={`situacion-label-${credit.id}`}>Situación</InputLabel>
                        <Select
                          labelId={`situacion-label-${credit.id}`}
                          id={`situacion-${credit.id}`}
                          value={credit.situacion}
                          label="Situación"
                          onChange={(e) => updateCredit(credit.id, 'situacion', e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="">Seleccionar</MenuItem>
                          <MenuItem value="Al corriente de pago">Al corriente de pago</MenuItem>
                          <MenuItem value="Impago">Impago</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      {credit.id !== 'default-empty' && (
                        <Button color="error" onClick={() => removeCredit(credit.id)} startIcon={<DeleteIcon />}>Eliminar</Button>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ mb: 3, maxHeight: 480 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Banco / Financiera</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Tipo Crédito</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Capital Pendiente</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Cuota Mensual</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Situación</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formData.creditos.map((credit) => (
                  <TableRow key={credit.id} sx={{ '&:nth-of-type(even)': { backgroundColor: '#fafafa' } }}>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={credit.banco}
                        onChange={(e) => updateCredit(credit.id, 'banco', e.target.value)}
                        placeholder="Ej: Caixabank"
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel shrink id={`tipo-credito-table-label-${credit.id}`}>Tipo Crédito</InputLabel>
                        <Select
                          labelId={`tipo-credito-table-label-${credit.id}`}
                          id={`tipo-credito-table-${credit.id}`}
                          value={credit.tipoCredito}
                          label="Tipo Crédito"
                          onChange={(e) => updateCredit(credit.id, 'tipoCredito', e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="">Seleccionar</MenuItem>

                          <MenuItem value="Tarjeta de crédito">Tarjeta de crédito</MenuItem>
                          <MenuItem value="Préstamo personal">Préstamo personal</MenuItem>
                          <MenuItem value="Crédito vehicular">Crédito vehicular</MenuItem>
                          <MenuItem value="Hipoteca">Hipoteca</MenuItem>
                          <MenuItem value="Otros">Otros</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        label="Capital Pendiente"
                        InputLabelProps={{ shrink: true }}
                        value={credit.capitalPendiente}
                        onChange={(e) => updateCredit(credit.id, 'capitalPendiente', e.target.value)}
                        placeholder="0,00"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">€</InputAdornment>,
                          inputMode: 'decimal',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        label="Cuota Mensual"
                        InputLabelProps={{ shrink: true }}
                        value={credit.cuotaMensual}
                        onChange={(e) => updateCredit(credit.id, 'cuotaMensual', e.target.value)}
                        placeholder="0,00"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">€</InputAdornment>,
                          inputMode: 'decimal',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel shrink id={`situacion-table-label-${credit.id}`}>Situación</InputLabel>
                        <Select
                          labelId={`situacion-table-label-${credit.id}`}
                          id={`situacion-table-${credit.id}`}
                          value={credit.situacion}
                          label="Situación"
                          onChange={(e) => updateCredit(credit.id, 'situacion', e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="">Seleccionar</MenuItem>
                          <MenuItem value="Al corriente de pago">Al corriente de pago</MenuItem>
                          <MenuItem value="Impago">Impago</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      {credit.id !== 'default-empty' && (
                        <IconButton
                          color="error"
                          onClick={() => removeCredit(credit.id)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : null}

      {/* Add Credit Button - moved to end of table */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addCredit}
          sx={{ mb: 2 }}
        >
          Agregar Crédito
        </Button>
      </Box>

      {/* Summary */}
      {formData.creditos.length > 0 && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Resumen de Créditos
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="textSecondary">
                  Total de Créditos: <strong>{formData.creditos.length}</strong>
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="textSecondary">
                  Capital Total Pendiente: <strong>
                    {new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(
                      formData.creditos.reduce((sum, credit) => {
                        const normalized = normalizeEuropeanInput(credit.capitalPendiente || '0');
                        return sum + (parseFloat(normalized) || 0);
                      }, 0)
                    )}
                  </strong>
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="textSecondary">
                  Cuota Mensual Total: <strong>
                    {new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(
                      formData.creditos.reduce((sum, credit) => {
                        const normalized = normalizeEuropeanInput(credit.cuotaMensual || '0');
                        return sum + (parseFloat(normalized) || 0);
                      }, 0)
                    )}
                  </strong>
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Acceptance & Submit */}
      <Box sx={{ mt: 3 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.acceptSubmitConfirmation}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                acceptSubmitConfirmation: e.target.checked,
              }))}
            />
          }
          label={
            <span>
              ENTIENDO Y ACEPTO el tratamiento de mis datos tal y como se describe posteriormente y se explica con mayor detalle en la{' '}
              <a
                href="https://www.easydebt.es/privacidad/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}
              >
                Política de Privacidad
              </a>.
              {' '}y acepta los{' '}
              <a
                href="https://www.easydebt.es/terminos-y-condiciones/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}
              >
                términos,
              </a>{' '}
              <a
                href="https://www.easydebt.es/terminos-y-condiciones/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}
              >
                condiciones y tarifas
              </a>.
            </span>
          }
          sx={{ alignItems: 'flex-start', mt: 1, '& .MuiCheckbox-root': { pt: 0 } }}
        />
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
          sx={{
            minWidth: '200px',
            height: '48px',
            fontSize: '1.1rem',
            fontWeight: 600,
          }}
        >
          {loading ? 'Enviando...' : 'Enviar Solicitud'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return renderPersonalDataStep();
      case 1:
        return renderProfessionalDataStep();
      case 2:
        return renderHousingDataStep();
      case 3:
        return renderCreditDataStep();
      default:
        return null;
    }
  };

  // Show loading while initializing authentication
  if (authLoading || preflightChecking) {
    return (
      <ThemeProvider theme={clientTheme}>
        <Container maxWidth="sm" sx={{ py: 4 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6">{authLoading ? 'Inicializando portal...' : 'Verificando permisos...'}</Typography>
          </Paper>
        </Container>
      </ThemeProvider>
    );
  }

  // If preflight failed, show a dedicated message with guidance and optional reference ID
  if (preflightPassed === false) {
    return (
      <ThemeProvider theme={clientTheme}>
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Paper sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom color="error">No se pudo verificar los permisos</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              No hemos podido confirmar permisos para guardar su solicitud. Por favor, cambie de navegador o de red (desactive VPN/proxy o pruebe con otra conexión) y vuelva a intentarlo.
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {preflightRefId && (
              <Typography variant="body2" color="textSecondary">
                Referencia: {preflightRefId}
              </Typography>
            )}
          </Paper>
        </Container>
      </ThemeProvider>
    );
  }

  if (success) {
    return (
      <ThemeProvider theme={clientTheme}>
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom color="success.main">
              {mortgageMode ? '¡Solicitud de Hipoteca Enviada!' : '¡Formulario Enviado Exitosamente!'}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Su solicitud ha sido registrada en nuestro sistema. Nuestro equipo la revisará y se pondrá en contacto con usted.
            </Typography>
          </Paper>
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={clientTheme}>
      <Container maxWidth={false} sx={{ py: 2, px: { xs: 1, sm: 2, md: 3 } }}>
        <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              {mortgageMode ? 'Formulario de Solicitud de Hipoteca' : 'Formulario de Registro'}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {mortgageMode
                ? 'Complete el formulario para evaluar su solicitud de hipoteca'
                : 'Complete el formulario para enviar su solicitud'}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}


          {/* Responsive Horizontal Stepper */}
          <Box sx={{ mb: 4 }}>
            <Stepper
              activeStep={activeStep}
              orientation="horizontal"
              sx={{
                justifyContent: 'center',
                '& .MuiStepConnector-root': {
                  top: { xs: 12, sm: 22 },
                },
                '& .MuiStep-root': {
                  padding: { xs: '0 4px', sm: '0 8px', md: '0 12px' },
                  flex: '0 1 auto',
                },
                '& .MuiStepLabel-root': {
                  flexDirection: 'column',
                  alignItems: 'center',
                },
                '& .MuiStepLabel-iconContainer': {
                  paddingRight: 0,
                  marginBottom: { xs: 0, sm: '8px' },
                },
                '& .MuiStepLabel-labelContainer': {
                  display: { xs: 'none', sm: 'block' },
                  textAlign: 'center',
                },
                '& .MuiStepLabel-label': {
                  fontSize: { sm: '0.75rem', md: '0.875rem' },
                  textAlign: 'center',
                },
                '& .MuiStepLabel-label.Mui-active': {
                  color: 'primary.main',
                  fontWeight: 600,
                },
                '& .MuiStepLabel-label.Mui-completed': {
                  color: 'primary.main',
                },
              }}
            >
              {portalSteps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel icon={step.icon}>
                    {step.label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>



          {/* Step Content */}
          <Box sx={{ mb: 4 }}>
            {renderStepContent(activeStep)}
          </Box>

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              variant="outlined"
            >
              Anterior
            </Button>

            <Box>
              {activeStep < portalSteps.length - 1 && (
                <Button
                  variant="contained"
                  onClick={handleNext}
                >
                  Siguiente
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default ClientPortal;
